import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { Listings } from '@/types/PropertyListings';
import { ChevronLeft, ChevronRight, Clock, Eye, Heart, Mail, Phone, Users } from 'lucide-react';
import React, { useEffect, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { format } from 'date-fns';
import Helper from '@/lib/helper';
import { toast } from 'sonner';
import api from '@/lib/axiosInstance';
import { useParams } from 'react-router-dom';


interface _Histories {
    listing: Listings;
}

const Histories : React.FC<_Histories> = ({
    listing
}) => {
    const { listingId } = useParams();
    const [histories, setHistories] = useState<any[]>([]);
    const [pagination, setPagination] = useState<any>(null);
    const [currentPage, setCurrentPage] = useState(1);

    const getListingHistories = async () => {
        try {
            const response = await api.get(`/listings/${listingId}/histories`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setHistories(response.data.histories);
            setPagination(response.data.pagination);
        } catch (err: any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    useEffect(() => {
        getListingHistories();
    }, []);
    
    return (
        <div className="space-y-4">
            <Card className="border-border">
                <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-medium">Recent Visitor Activity</CardTitle>
                        <Badge variant="outline" className="text-xs">
                            {pagination?.totalCount || 0} total visits
                        </Badge>
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    {histories.length === 0 ? (
                        <div className="text-center py-12">
                            <Users className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
                            <p className="text-sm text-muted-foreground">No visitor activity yet</p>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3">
                                {
                                    histories.map((log) => (
                                        <div key={log.id} className="border border-border rounded-md p-4 hover:bg-muted/30 transition-colors">
                                            <div className="md:flex items-start justify-between">
                                                <div className="flex items-start gap-3 flex-1">
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <div className="cursor-pointer">
                                                                <Avatar className="h-10 w-10 ring-2 ring-border hover:ring-primary transition-all">
                                                                    <AvatarImage 
                                                                        src={log.consumer?.avatar ? `${import.meta.env.VITE_AWS_S3}/${log.consumer.avatar}` : undefined} 
                                                                    />
                                                                    <AvatarFallback className="bg-muted text-foreground text-xs">
                                                                        {log.consumer 
                                                                            ? `${log.consumer.firstName?.[0] || ''}${log.consumer.lastName?.[0] || ''}` 
                                                                            : 'AN'}
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                            </div>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-80" align="start">
                                                            <div className="space-y-3">
                                                                <div className="flex items-center gap-3">
                                                                    <Avatar className="h-12 w-12">
                                                                        <AvatarImage 
                                                                            src={log.consumer?.avatar ? `${import.meta.env.VITE_AWS_S3}/${log.consumer.avatar}` : undefined} 
                                                                        />
                                                                        <AvatarFallback>
                                                                            {log.consumer 
                                                                                ? `${log.consumer.firstName?.[0] || ''}${log.consumer.lastName?.[0] || ''}` 
                                                                                : 'AN'}
                                                                        </AvatarFallback>
                                                                    </Avatar>
                                                                    <div className="flex-1">
                                                                        <h4 className="font-semibold text-sm">
                                                                            {log.consumer 
                                                                                ? `${log.consumer.firstName} ${log.consumer.lastName}`
                                                                                : 'Anonymous User'}
                                                                        </h4>
                                                                        {!log.consumer && (
                                                                            <p className="text-xs text-muted-foreground">Guest Visitor</p>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <Separator />
                                                                {
                                                                    log.consumer ? (
                                                                        <div className="space-y-2">
                                                                            <div className="flex items-center gap-2 text-sm">
                                                                                <Mail size={14} className="text-muted-foreground" />
                                                                                <span className="text-muted-foreground text-sm">{log.consumer.email}</span>
                                                                            </div>
                                                                            {log.consumer.contact && (
                                                                                <div className="flex items-center gap-2 text-sm">
                                                                                    <Phone size={14} className="text-muted-foreground" />
                                                                                    <span className="text-muted-foreground text-sm">{log.consumer.contact}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-xs text-muted-foreground">
                                                                            <p>This visitor browsed anonymously</p>
                                                                            <p className="mt-1">UUID: {log.uuId}</p>
                                                                        </div>
                                                                    )
                                                                }
                                                                <Separator />
                                                                <div className="space-y-2">
                                                                    <div className="flex items-center justify-between text-sm">
                                                                        <span className="text-muted-foreground">Total Views</span>
                                                                        <span className="font-semibold">{log.history?.viewCount || 1}</span>
                                                                    </div>
                                                                    <div className="flex items-center justify-between text-sm">
                                                                        <span className="text-muted-foreground">Last viewed at</span>
                                                                        <div>{format(new Date(log.history.lastViewedAt), 'MMM dd, yyyy HH:mm')}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </PopoverContent>
                                                    </Popover>
                                                    
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <h4 className="text-sm font-medium text-foreground">
                                                                {log.consumer 
                                                                    ? `${log.consumer.firstName} ${log.consumer.lastName}`
                                                                    : 'Anonymous User'}
                                                            </h4>
                                                            {
                                                                log.consumer && (
                                                                    <Badge variant="secondary" className="text-xs">Registered</Badge>
                                                                )
                                                            }
                                                            {
                                                                log.history?.isFavorite === 1 && (
                                                                    <Heart size={12} className="fill-red-500 text-red-500" />
                                                                )
                                                            }
                                                        </div>
                                                        
                                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                                                            <div className="flex items-center gap-1">
                                                                <Clock size={12} />
                                                            </div>
                                                            <span>•</span>
                                                            <div className="flex items-center gap-1">
                                                                <Eye size={12} />
                                                                <span>{log.timeSpent}s on page</span>
                                                            </div>
                                                            {
                                                                log.history?.viewCount > 1 && (
                                                                    <>
                                                                        <span>•</span>
                                                                        <span className="font-medium">{log.history.viewCount} visits</span>
                                                                    </>
                                                                )
                                                            }
                                                        </div>
                                                        
                                                        <div className="flex items-center gap-2 mt-2 text-xs">
                                                            {
                                                                log.startTime && (
                                                                    <Badge variant="outline" className="text-xs">
                                                                        {format(new Date(log.startTime), 'MMM dd, yyyy HH:mm')}
                                                                    </Badge>
                                                                )
                                                            }
                                                            {
                                                                log.endTime && (
                                                                    <>
                                                                        <span className="text-muted-foreground">→</span>
                                                                        <Badge variant="outline" className="text-xs">
                                                                            {format(new Date(log.endTime), 'HH:mm')}
                                                                        </Badge>
                                                                    </>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                                {
                                                    log.consumer && (
                                                        <div className="flex justify-end gap-2">
                                                            <Button 
                                                            size="sm" 
                                                            variant="outline" 
                                                            className="w-8 p-0"
                                                            onClick={() => window.location.href = `mailto:${log.consumer.email}`}>
                                                                <Mail size={14} />
                                                            </Button>
                                                            {
                                                                log.consumer.contact && (
                                                                    <Button 
                                                                    size="sm" 
                                                                    variant="outline" 
                                                                    className="w-8 p-0"
                                                                    onClick={() => window.location.href = `tel:${log.consumer.contact}`}>
                                                                        <Phone size={14} />
                                                                    </Button>
                                                                )
                                                            }
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                            {
                                pagination && pagination.totalPages > 1 && (
                                    <div className="flex items-center justify-between pt-4 border-t border-border">
                                        <p className="text-xs text-muted-foreground">
                                            Page {pagination.currentPage} of {pagination.totalPages}
                                        </p>
                                        <div className="flex gap-2">
                                            <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={!pagination.hasPreviousPage}
                                            >
                                                <ChevronLeft size={16} />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setCurrentPage(prev => prev + 1)}
                                                disabled={!pagination.hasNextPage}
                                            >
                                                <ChevronRight size={16} />
                                            </Button>
                                        </div>
                                    </div>
                                )
                            }
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

export default Histories